/**
 * SimplyLoan Microsoft Advertising (Bing Ads) API client.
 *
 * GET /api/simplyloan/microsoft-dashboard returns cohort revenue: every number on a row belongs to the
 * leads that row acquired in the selected dates, whenever the commission was earned. Money is DKK.
 * Also returns top Search Queries and supports live campaign/adgroup status, budget, and negative keyword controls.
 */
import { API_BASE_URL } from '../config';

export type MicrosoftGeo = 'all' | 'se' | 'no' | 'fi';
export type MicrosoftLevel = 'campaign' | 'adgroup' | 'ad';

export interface MicrosoftDashboardParams {
  geo: MicrosoftGeo;
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  level: MicrosoftLevel;
}

export interface MicrosoftDashboardRow {
  key: string | null;               // campaign_id, adgroup_id or ad_id; null = Microsoft leads without an object id
  name: string;
  campaign_id: string | null;
  campaign_name: string | null;
  adgroup_id: string | null;
  adgroup_name: string | null;
  account_id: string | null;
  campaign_type: string | null;
  bidding_strategy_type: string | null;
  geo: string | null;
  status: string | null;            // Active | Paused
  editorial_status: string | null;  // Active | Disapproved | ActiveLimited
  daily_budget_account: number | null;
  spend_dkk: number;
  fx_missing: boolean;
  impressions: number;
  clicks: number;
  microsoft_conversions: number;       // as Microsoft Advertising reports them
  microsoft_conversions_value: number;
  search_impression_share: number | null;
  leads: number;                       // our leads (web form + msclkid click-outs)
  rev_d0: number;                      // approved commission within 24 h of acquisition
  rev_d7: number;
  rev_d14: number;
  rev_d30: number;
  rev_d90: number;                     // to date, capped at 90 days
  rev_pending: number;
  leads_mature_d30: number;
  projected_d30: number | null;
  cpl_dkk: number | null;
  cpc_dkk: number | null;
  ctr: number | null;
  drip_revenue_dkk: number;
  immediate_roas: number | null;
  ltv30_roas: number | null;
  projected_ltv30_roas: number | null;
  roas_to_date: number | null;
}

export interface MicrosoftSearchTermRow {
  search_term: string;
  campaign_id: string;
  campaign_name: string;
  adgroup_id: string;
  adgroup_name: string;
  account_id: string;
  geo: string | null;
  keyword_text: string;
  keyword_match_type: string;
  search_term_status: string;
  impressions: number;
  clicks: number;
  cpc_dkk: number | null;
  ctr: number | null;
  spend_dkk: number;
  conversions: number;
  conversions_value: number;
  cpa_dkk: number | null;
}

export interface MicrosoftDashboardKpis {
  spend_dkk: number;
  impressions: number;
  clicks: number;
  cpc_dkk: number | null;
  ctr: number | null;
  leads: number;
  microsoft_reported_conversions: number;
  cpl_dkk: number | null;
  frontend_revenue_dkk: number;
  drip_revenue_dkk: number;
  total_revenue_dkk: number;
  pending_revenue_dkk: number;
  blended_roas: number | null;
  net_margin_dkk: number;
  projected_d30_revenue_dkk: number | null;
  unmapped_spend_dkk: number;
  fx_missing: boolean;
}

export interface MicrosoftConversionHealth {
  hours: number;
  by_status: Record<string, number>;
  by_event: Record<string, Record<string, number>>;
  attempted: number;
  error_rate: number | null;
  msclkid_match_share: number | null;
  ec_match_share: number | null;
  strong_match_share: number | null;
  auth_errors: number;
  backlog_over_60m: number;
}

export interface MicrosoftDashboardResponse {
  filters: { geo: string; level: MicrosoftLevel; from: string; to: string; timezone: string };
  currency: 'DKK';
  kpis: MicrosoftDashboardKpis;
  rows: MicrosoftDashboardRow[];
  search_terms: MicrosoftSearchTermRow[];
  d30_multipliers: Record<string, number>;
  health: { conversions_24h: MicrosoftConversionHealth; synced_at: Record<string, string | null> };
  generated_at: string;
}

export async function fetchMicrosoftDashboard(
  params: MicrosoftDashboardParams,
  signal?: AbortSignal,
): Promise<MicrosoftDashboardResponse> {
  const qs = new URLSearchParams({ geo: params.geo, from: params.from, to: params.to, level: params.level });
  const res = await fetch(`${API_BASE_URL}/api/simplyloan/microsoft-dashboard?${qs.toString()}`, { signal });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Microsoft Ads dashboard request failed (${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`);
  }
  return res.json();
}

// ─── Campaign builder & live controls ────────────────────────────────────────

export type PlanStatus =
  | 'draft' | 'validated' | 'queued' | 'building' | 'dry_run' | 'built' | 'failed' | 'active' | 'paused';

export interface PlanFinding {
  level: 'error' | 'warning';
  field: string;
  message: string;
  match: string;
}

export interface PlanPreviewAd {
  ad_group: string;
  ad: string;
  headlines: string[];
  descriptions: string[];
  path1: string;
  path2: string;
  final_url: string | null;
  keywords_count: number;
  sample_keywords: string[];
}

export interface PlanValidation {
  ok: boolean;
  errors: PlanFinding[];
  warnings: PlanFinding[];
  preview: PlanPreviewAd[];
  daily_budget: number | null;
  currency: string | null;
}

export interface CampaignPlanRow {
  id: number;
  plan_key: string;
  geo: string;
  name: string;
  status: PlanStatus;
  validation: { ok: boolean; errors: PlanFinding[]; warnings: PlanFinding[]; daily_budget: number | null; currency: string | null } | null;
  last_error: string | null;
  created_by: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
  built_at: string | null;
  activated_at: string | null;
  objects: number;
  campaign_id: string | null;
  spec: Record<string, unknown>;
}

export interface PlanListResponse {
  mode: 'dry_run' | 'live';
  activation_allowed: boolean;
  plans: CampaignPlanRow[];
}

export interface PlanTemplate {
  file: string;
  spec: Record<string, unknown>;
}

export interface PlanActionResponse extends Partial<PlanValidation> {
  status: string;
  plan_id?: number;
  reason?: string;
  activated?: number;
  paused?: number;
  created_objects?: number;
}

export interface LiveControlResponse {
  status: string;
  reason?: string;
  error?: string;
  account_id?: string;
  object_type?: string;
  object_id?: string;
  new_status?: string;
  campaign_id?: string;
  daily_budget?: number;
  keyword?: string;
  match_type?: string;
}

async function campaignsRequest<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit = body === undefined
    ? {}
    : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
  const res = await fetch(`${API_BASE_URL}/api/simplyloan/microsoft-campaigns${path}`, init);
  const text = await res.text();
  let parsed: any = {};
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    parsed = { error: text.slice(0, 200) };
  }
  if (!res.ok && res.status !== 409) {
    throw new Error(parsed?.error || parsed?.reason || `Request failed (${res.status})`);
  }
  return parsed as T;
}

export const listMicrosoftCampaignPlans = () => campaignsRequest<PlanListResponse>('');
export const getMicrosoftCampaignTemplates = () => campaignsRequest<{ templates: PlanTemplate[] }>('/templates');
export const validateMicrosoftCampaignPlan = (plan: unknown) => campaignsRequest<PlanValidation>('/validate', { plan });
export const saveMicrosoftCampaignPlan = (plan: unknown) => campaignsRequest<PlanActionResponse>('', { plan });
export const microsoftCampaignPlanAction = (action: 'queue' | 'build-now' | 'activate' | 'pause', planId: number) =>
  campaignsRequest<PlanActionResponse>(`/${action}`, { plan_id: planId });

export const updateMicrosoftLiveStatus = (params: {
  geo?: string;
  account_id?: string;
  campaign_id?: string;
  object_type: 'campaign' | 'adgroup';
  object_id: string;
  status: 'Active' | 'Paused';
}) => campaignsRequest<LiveControlResponse>('/live-status', params);

export const updateMicrosoftLiveBudget = (params: {
  geo?: string;
  account_id?: string;
  campaign_id: string;
  daily_budget: number;
}) => campaignsRequest<LiveControlResponse>('/live-budget', params);

export const addMicrosoftNegativeKeyword = (params: {
  geo?: string;
  account_id?: string;
  campaign_id?: string;
  adgroup_id?: string;
  keyword_text: string;
  match_type?: 'Exact' | 'Phrase';
}) => campaignsRequest<LiveControlResponse>('/negative-keyword', params);

export const triggerMicrosoftSyncNow = (days: number = 7) =>
  campaignsRequest<Record<string, unknown>>('/sync-now', { days });

export const triggerMicrosoftDispatchNow = () =>
  campaignsRequest<Record<string, unknown>>('/dispatch-now', {});
